// ============================================================================
// PAYMENT SERVICE — unified Payment Voucher (ADR-0039). Posts on create.
// ============================================================================

import { get, post, del } from '../client/axiosClient';
import { paymentEndpoints } from '../endpoints/accounts.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, EmptyResponse, ID,
  PaymentDto, CreatePaymentRequest, PaymentQueryParams, OutstandingOpenItemDto, PayablePartyDto,
  DeductionReasonDto,
} from '../types';

class PaymentService {
  async getAll(params?: PaymentQueryParams): Promise<PaginatedResponse<PaymentDto>> {
    const endpoint = params ? buildEndpointWithQuery(paymentEndpoints.list(), params) : paymentEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<PaymentDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /** Full voucher including its lines — the list response carries headers only. */
  async getById(id: ID): Promise<PaymentDto> {
    const response = await get<ApiResponse<PaymentDto>>(paymentEndpoints.getById(id));
    return response.data.data;
  }

  /**
   * A supplier's unpaid bills. Party-first: Fairbooks keeps a ledger per party rather
   * than an AP control account, so the caller names the supplier, not an account.
   * Empty means nothing is owed to them.
   */
  async getOpenBills(partyId: ID): Promise<OutstandingOpenItemDto[]> {
    const url = buildEndpointWithQuery(paymentEndpoints.openBills(), { partyId });
    const response = await get<ApiResponse<OutstandingOpenItemDto[]>>(url);
    return Array.from(response.data.data || []);
  }

  /** Why a bill can be withheld (ADR-0060) — the Reason picker. */
  async getDeductionReasons(): Promise<DeductionReasonDto[]> {
    const response = await get<ApiResponse<DeductionReasonDto[]>>(paymentEndpoints.deductionReasons());
    return Array.from(response.data.data || []);
  }

  /**
   * Parties we currently owe, largest first — the picker's default list. Filters by debt
   * rather than by role, so refunds and unclassified suppliers both appear.
   */
  async getPayableParties(searchTerm?: string, limit = 25): Promise<PayablePartyDto[]> {
    const url = buildEndpointWithQuery(paymentEndpoints.payableParties(), { searchTerm, limit });
    const response = await get<ApiResponse<PayablePartyDto[]>>(url);
    return Array.from(response.data.data || []);
  }

  async create(data: CreatePaymentRequest): Promise<PaymentDto> {
    const response = await post<ApiResponse<PaymentDto>>(paymentEndpoints.create(), data);
    return response.data.data;
  }

  async cancel(id: ID, rowVersion: number, reason?: string): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(paymentEndpoints.cancel(id), reason ? { rowVersion, reason } : { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }
}

export const paymentService = new PaymentService();
export default paymentService;
